/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject } from 'preact';
import { useCallback, useRef } from 'preact/hooks';
import type { Dispatch } from 'preact/hooks';
import type { TimeReducerAction } from './timeReducer';
import type { TimeFieldState } from './getTimeFieldInitialState';
import type { EditableTimeSegmentType } from './types';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { isBackspaceOrDelete, isSelectAll } from '#utils/PRIVATE_keyboardUtils';
import { useDoubleTap } from '#hooks/UNSAFE_useDoubleTap';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useInputMaskSelectFirstHandler } from '#PRIVATE_InputMaskCommon/useInputMaskSelectFirstHandler';

type Props = {
  direction: 'ltr' | 'rtl';
  dispatch: Dispatch<TimeReducerAction>;
  groupRef?: RefObject<HTMLDivElement>;
  isDisabled?: boolean;
  isInputFocused?: boolean;
  state: TimeFieldState;
};

function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}

/**
 * A hook that creates various handlers for a TimeField.
 * This is extremely similar to useDateFieldHandler, and it shouldn't be too hard to share code.
 *
 * This hook creates a timeClearedRef. It is set to false by default and whenever the
 * currentTime has a value, including a partial value.
 * When the user's action is to clear the field, this hook dispatches the 'clearTime' action,
 * and sets timeClearedRef to true.
 * timeClearedRef is returned from the hook so that the aria-live DOM can be updated to say Time Cleared
 * or to clear this text.
 */
const useTimeFieldHandlers = ({
  direction,
  dispatch,
  groupRef,
  isDisabled,
  isInputFocused,
  state
}: Props) => {
  const {
    tabbableModeProps: { tabIndex: tabbableModeIndex }
  } = useTabbableMode();
  const anySegmentSelected = state.segments.some((segment) => !!segment.isSelected);

  // If any segment is selected, it is contenteditable=true and it has the focus. If you shift-Tab, you will
  // land on the outer div (TimeField) first meaning it takes 2 shift-tab keys to get the previous element.
  // To prevent this, if any segment is selected the tabindex on the div is set to -1.
  // We also need to set tabindex to -1 for the disabled case, since we are dealing with a div instead
  // of an intrinsic input and have to handle it directly.
  const updatedTabIndex = tabbableModeIndex === -1 || anySegmentSelected || isDisabled ? -1 : 0;

  // Keep track whether the time was cleared.
  const timeClearedRef = useRef<boolean>(false);

  // The timeClearedRef.current is set to true when the user does a clear all.
  // We set it back to false whenever the currentTime has a value.
  if (state.currentTime != null && Object.keys(state.currentTime).length > 0) {
    timeClearedRef.current = false;
  }

  // Handle requested segment changes by calling the reducer dispatch function.
  const onChange = useCallback(
    (action: TimeReducerAction) => {
      dispatch(action);
    },
    [dispatch]
  );

  // If the user clicked on a segment, dispatch a request to select it.
  // If they clicked on the time field itself, select the first segment.
  const onClick = useCallback(
    (e: Event) => {
      const segmentType = (e.target as HTMLElement).getAttribute(
        'data-segment'
      ) as EditableTimeSegmentType;
      if (segmentType !== null) {
        dispatch({ actionType: 'selectSegment', data: { type: segmentType } });
      } else {
        dispatch({ actionType: 'selectFirst' });
      }
    },
    [dispatch]
  );

  // If complete, select the time.
  const selectAll = useCallback(
    (isGroupFocus = true) => {
      if (state.isCompleteTime) {
        dispatch({ actionType: 'selectTime' });
        // Focus can end up in different places depending on if the user selected a segment
        // first (via click or arrow key), and then did select all. To make shift-tabbing
        // consistent in all cases, set focus to the group when selecting the time.
        isGroupFocus && groupRef?.current?.focus();
      }
    },
    [dispatch, groupRef, state.isCompleteTime]
  );

  const onDoubleClick = useCallback(() => {
    selectAll();
  }, [selectAll]);

  const onDoubleTap = useCallback(
    (e: Event) => {
      const segmentType = (e.target as HTMLElement).getAttribute(
        'data-segment'
      ) as EditableTimeSegmentType;

      // Only do select all if a segment was double tapped, which means the
      // mobile keyboard will stay up.
      !!segmentType && selectAll(false);
    },
    [selectAll]
  );

  const doubleTapHandlers = useDoubleTap({ onDoubleTap: onDoubleTap, onSingleTap: onClick });

  // Handle arrow key navigation between segments. If no segment is currently selected, then select the first one.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        (direction === 'ltr' && event.key === 'ArrowLeft') ||
        (direction === 'rtl' && event.key === 'ArrowRight')
      ) {
        // Select the previous segment when pressing Left arrow (or Right arrow in rtl).
        dispatch({ actionType: 'selectPrevious' });
        event.stopPropagation();
      } else if (
        (direction === 'ltr' && event.key === 'ArrowRight') ||
        (direction === 'rtl' && event.key === 'ArrowLeft')
      ) {
        // Select the next segment when pressing Right arrow (or Left arrow in rtl).
        dispatch({ actionType: 'selectNext' });
        event.stopPropagation();
      } else if (
        (isBackspaceOrDelete(event) || event.key === 'Backspace') &&
        state.isTimeSelected
      ) {
        // Clear the entire date.
        dispatch({ actionType: 'clearTime' });
        timeClearedRef.current = true;
        // Kill the event to prevent weird issue with placeholders getting deleted on iOS only.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      } else if (isSelectAll(event)) {
        selectAll();
        event.preventDefault();
        event.stopPropagation();
      } else if (event.key === 'Enter') {
        // Hitting Enter commits any changes.
        // This is also where we set 'AM' if hour is set, and AM isn't set.
        // This is where we set state.isCommitting = true.
        dispatch({ actionType: 'commitOnEnter' });
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [direction, dispatch, selectAll, state.isTimeSelected]
  );

  const onPointerDown = useInputMaskSelectFirstHandler({ dispatch, isInputFocused });

  const handlers = isMobile()
    ? { ...doubleTapHandlers, onKeyDown }
    : { onClick, onDoubleClick, onKeyDown, onPointerDown };

  return {
    timeClearedRef,
    timeFieldHandlers: isDisabled ? {} : handlers,
    segmentHandlers: isDisabled ? {} : { onChange },
    tabIndex: updatedTabIndex
  };
};

export { useTimeFieldHandlers };
